import React, { Fragment } from 'react';
import { ArrowLeftIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useCollaboratorByIdQuery } from '@/features/Collaborators/Data';
import { CollaboratorInfoHeader } from '@/features/Collaborators/Shared/CollaboratorInfoHeader';
import {
  CollaboratorAllocationForm,
  CollaboratorAllocationFormValues,
  ConfirmarEquipamentoModal,
  MotivoEncerramentoModal,
  type MotivoEncerramentoAlocacao,
} from '@/features/Collaborators/Allocations/components';
import {
  useCreateCollaboratorAllocationMutation,
  useCollaboratorAllocationQuery,
  useCollaboratorAllocationsQuery,
  useUpdateCollaboratorAllocationMutation,
  useCancelCollaboratorAllocationMutation,
} from '@/features/Collaborators/Allocations/data';
import { useToast } from '@/hooks/useToast';
import { CollaboratorCombobox } from '@/components/shared';
import { AllocationStatuses } from '@/features/Collaborators/Models';
import { Dialog, Transition } from '@headlessui/react';
import NovaSolicitacaoDrawer from '@/features/SI/pages/SolicitacoesListPage/components/NovaSolicitacaoDrawer';
import SolicitacaoDrawer from '@/features/SI/pages/SolicitacoesListPage/components/SolicitacaoDrawer';
import type { ValoresIniciaisNovaSolicitacao } from '@/features/SI/pages/NovaSolicitacaoPage/novaSolicitacaoContent.types';

const ALLOCATION_STATUS_LABELS: Record<string, string> = {
  [AllocationStatuses.Active]: 'Ativa',
  [AllocationStatuses.Cancelled]: 'Cancelada',
  [AllocationStatuses.Finished]: 'Concluída',
  [AllocationStatuses.Scheduled]: 'Agendada',
};

interface EtapaPosSalvarWizard {
  etapa:
    | 'motivo'
    | 'confirmarRecolhimento'
    | 'confirmarEquipamentoNovo'
    | 'confirmarTrocaEquipamento'
    | 'confirmarFormatacao';
  alocacaoId: string;
  clienteId: string | null;
  clienteNome: string | null;
  // Só preenchido em 'confirmarTrocaEquipamento' (Mudança de Projeto) — Cliente da
  // alocação de ORIGEM (a que foi encerrada), pra pré-preencher o Cliente da devolução
  // na Solicitação (ver abrirNovaSolicitacao/clienteDevolucaoId).
  clienteOrigemId?: string | null;
  clienteOrigemNome?: string | null;
}

export default function CollaboratorAllocationFormPage() {
  const { id: collaboratorId, allocationId } = useParams<{
    id?: string;
    allocationId?: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { success: toastSuccess, error: toastError } = useToast();
  const createAllocationMutation = useCreateCollaboratorAllocationMutation();
  const updateAllocationMutation = useUpdateCollaboratorAllocationMutation();
  const cancelAllocationMutation = useCancelCollaboratorAllocationMutation();
  const isEditMode = Boolean(allocationId);

  // Presente quando esse "Registrar Alocação" é a continuação de uma Mudança de Projeto
  // (ver handleSubmit/wizard abaixo — o usuário veio de "encerrar uma alocação" escolhendo
  // esse motivo, e precisa criar a alocação nova antes de decidirmos se abre Solicitação).
  // Cliente de origem vai junto só pra pré-preencher o Cliente da devolução na Solicitação
  // (ver clienteOrigemId em EtapaPosSalvarWizard).
  const mudancaDeProjetoState = location.state as {
    mudancaDeProjetoOrigemAlocacaoId?: string;
    mudancaDeProjetoClienteOrigemId?: string | null;
    mudancaDeProjetoClienteOrigemNome?: string | null;
  } | null;
  const mudancaDeProjetoOrigemAlocacaoId = mudancaDeProjetoState?.mudancaDeProjetoOrigemAlocacaoId ?? null;
  const [wizard, setWizard] = React.useState<EtapaPosSalvarWizard | null>(null);
  // Prefill da Solicitação aberta a partir do wizard — abre como drawer lateral (mesmo
  // componente usado na lista do SI), sem navegar pra `/si/nova`.
  const [novaSolicitacaoDrawer, setNovaSolicitacaoDrawer] =
    React.useState<ValoresIniciaisNovaSolicitacao | null>(null);
  // Acompanhamento da Solicitação recém-criada — também um drawer (mesmo componente da
  // lista do SI), pra não sair da página de Alocações depois de criar.
  const [acompanharSolicitacaoId, setAcompanharSolicitacaoId] = React.useState<string | null>(null);

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const [selectedCollaboratorId, setSelectedCollaboratorId] = React.useState(collaboratorId ?? '');
  React.useEffect(() => {
    if (collaboratorId) {
      setSelectedCollaboratorId(collaboratorId);
    }
  }, [collaboratorId]);
  const activeCollaboratorId = React.useMemo(() => {
    if (isEditMode) {
      return collaboratorId ?? undefined;
    }
    return selectedCollaboratorId || collaboratorId || undefined;
  }, [collaboratorId, selectedCollaboratorId, isEditMode]);

  const {
    data: collaborator,
    isLoading,
    isError,
    error,
  } = useCollaboratorByIdQuery(activeCollaboratorId);

  const {
    data: allocation,
    isLoading: isLoadingAllocation,
    isError: isAllocationError,
    error: allocationError,
  } = useCollaboratorAllocationQuery(activeCollaboratorId, allocationId);

  // Usado só pra decidir a descrição de "Saída do Projeto" no wizard (ver
  // temOutrasAlocacoesAtivas abaixo) — se o colaborador tem outra alocação ativa além
  // desta, "sem projeto definido" seria uma informação errada.
  const { data: todasAlocacoes = [] } = useCollaboratorAllocationsQuery(activeCollaboratorId);
  const temOutrasAlocacoesAtivas = wizard
    ? todasAlocacoes.some(
        (item) => item.id !== wizard.alocacaoId && item.status === AllocationStatuses.Active
      )
    : false;

  const handleBack = () => navigate(-1);
  const [isCancelDialogOpen, setCancelDialogOpen] = React.useState(false);
  const [cancelReason, setCancelReason] = React.useState('');

  const handleSubmit = async (values: CollaboratorAllocationFormValues) => {
    const targetCollaboratorId = activeCollaboratorId;

    if (!targetCollaboratorId) {
      toastError(
        isEditMode ? 'Erro ao atualizar alocação' : 'Erro ao registrar alocação',
        'Selecione um colaborador antes de continuar.'
      );
      throw new Error('Colaborador não informado.');
    }

    // Detectado ANTES de salvar, com o valor que já estava carregado (`allocation`) — se a
    // data de término estava vazia e passou a ter valor nesta edição, é o gatilho pra
    // perguntar o motivo (ver Discovery da integração com Alocações). Edições que não
    // mexem nisso (ex. só troca o responsável) mantêm o comportamento de sempre.
    const encerrouAgora = isEditMode && !allocation?.endDate && Boolean(values.endDate);

    try {
      if (isEditMode && allocationId) {
        await updateAllocationMutation.mutateAsync({
          collaboratorId: targetCollaboratorId,
          allocationId,
          values,
        });
        toastSuccess('Alocação atualizada', 'Os dados foram salvos com sucesso.');

        if (encerrouAgora) {
          setWizard({
            etapa: 'motivo',
            alocacaoId: allocationId,
            clienteId: values.clientId ?? null,
            clienteNome: values.clientName ?? null,
          });
          return; // o wizard decide pra onde navegar
        }
      } else {
        const novaAlocacaoId = await createAllocationMutation.mutateAsync({
          collaboratorId: targetCollaboratorId,
          values,
        });
        toastSuccess('Alocação registrada', 'Os dados foram salvos com sucesso.');

        setWizard({
          etapa: mudancaDeProjetoOrigemAlocacaoId ? 'confirmarTrocaEquipamento' : 'confirmarEquipamentoNovo',
          alocacaoId: novaAlocacaoId,
          clienteId: values.clientId ?? null,
          clienteNome: values.clientName ?? null,
          clienteOrigemId: mudancaDeProjetoState?.mudancaDeProjetoClienteOrigemId ?? null,
          clienteOrigemNome: mudancaDeProjetoState?.mudancaDeProjetoClienteOrigemNome ?? null,
        });
        return; // o wizard decide pra onde navegar
      }
      navigate(`/collaborators/${targetCollaboratorId}/allocations`);
    } catch (err) {
      console.error('[allocation-form] submit error', err);
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Tente novamente em alguns instantes';
      toastError(
        isEditMode ? 'Erro ao atualizar alocação' : 'Erro ao registrar alocação',
        message
      );
      throw err;
    }
  };
  const finalizarWizardSemSolicitacao = () => {
    setWizard(null);
    if (activeCollaboratorId) {
      navigate(`/collaborators/${activeCollaboratorId}/allocations`);
    }
  };

  const abrirNovaSolicitacao = (params: {
    tipoSolicitacao: string;
    clienteId: string | null;
    clienteNome: string | null;
    alocacaoId: string;
    // Só usado em Mudança de Projeto — Cliente da alocação de origem, pra pré-preencher o
    // Cliente da devolução na Solicitação (ver ValoresIniciaisNovaSolicitacao).
    clienteDevolucaoId?: string | null;
    clienteDevolucaoNome?: string | null;
  }) => {
    if (!activeCollaboratorId) return;
    setWizard(null);
    setNovaSolicitacaoDrawer({
      tipoSolicitacao: params.tipoSolicitacao,
      colaboradorId: activeCollaboratorId,
      clienteId: params.clienteId,
      clienteNome: params.clienteNome,
      alocacaoReferenciadaId: params.alocacaoId,
      clienteDevolucaoId: params.clienteDevolucaoId,
      clienteDevolucaoNome: params.clienteDevolucaoNome,
    });
  };

  const handleOpenCancelDialog = () => {
    setCancelReason('');
    setCancelDialogOpen(true);
  };

  const handleCloseCancelDialog = () => {
    if (cancelAllocationMutation.isLoading) {
      return;
    }

    setCancelDialogOpen(false);
    setCancelReason('');
  };

  const handleConfirmCancelAllocation = async () => {
    const targetCollaboratorId = activeCollaboratorId;

    if (!targetCollaboratorId || !allocationId) {
      toastError('Erro ao cancelar alocação', 'Dados insuficientes para concluir o cancelamento.');
      return;
    }

    try {
      await cancelAllocationMutation.mutateAsync({
        collaboratorId: targetCollaboratorId,
        allocationId,
        reason: cancelReason,
      });
      toastSuccess('Alocação cancelada', 'O status da alocação foi atualizado para cancelada.');
      setCancelDialogOpen(false);
      setCancelReason('');
      navigate(`/collaborators/${targetCollaboratorId}/allocations`);
    } catch (err) {
      console.error('[allocation-form] cancel error', err);
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Tente novamente em alguns instantes.';
      toastError('Erro ao cancelar alocação', message);
    }
  };

  const toDate = React.useCallback((value: any): Date | null => {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return value;
    }
    if (typeof value.toDate === 'function') {
      return value.toDate();
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, []);

  const allocationInitialValues = React.useMemo(() => {
    if (!allocation) {
      return null;
    }

    return {
      allocationType: allocation.allocationType,
      durationType: allocation.durationType,
      bucketType: allocation.bucketType,
      bucket: allocation.bucket ?? 0,
      startDate: toDate(allocation.startDate) ?? new Date(),
      endDate: toDate(allocation.endDate),
      clientId: allocation.clientId ?? null,
      clientName: allocation.clientName ?? null,
      clientLogo: allocation.clientLogo ?? null,
      clientResponsible: allocation.clientResponsible ?? null,
      responsibleId: allocation.responsibleId ?? null,
      responsibleName: allocation.responsibleName ?? null,
      responsibleAvatar: allocation.responsibleAvatar ?? null,
      departmentId: allocation.departmentId ?? null,
      departmentName: allocation.departmentName ?? null,
      projectId: allocation.projectId ?? null,
      projectName: allocation.projectName ?? null,
      capacityId: allocation.capacityId ?? null,
      capacityLabel: allocation.capacityLabel ?? null,
      capacityTypeId: allocation.capacityTypeId ?? null,
      capacityTypeName: allocation.capacityTypeName ?? null,
      capacityTechnologyId: allocation.capacityTechnologyId ?? null,
      capacityTechnologyName: allocation.capacityTechnologyName ?? null,
      capacitySeniorityId: allocation.capacitySeniorityId ?? null,
      capacitySeniorityName: allocation.capacitySeniorityName ?? null,
      rate: allocation.rate ?? null,
      noticeReason: allocation.noticeReason ?? null,
    } satisfies CollaboratorAllocationFormValues;
  }, [allocation, toDate]);

  const terminationAppliedAt = React.useMemo(
    () => (allocation ? toDate(allocation.terminationAppliedAt) : null),
    [allocation, toDate]
  );

  const formIsSaving = isEditMode
    ? updateAllocationMutation.isLoading
    : createAllocationMutation.isLoading;

  const comboboxSelectedId = selectedCollaboratorId || collaboratorId || null;
  const allocationStatus = allocation?.status ?? null;
  const isAllocationCancelled = allocationStatus === AllocationStatuses.Cancelled;
  const allocationStatusLabel = allocationStatus
    ? ALLOCATION_STATUS_LABELS[allocationStatus] ?? allocationStatus
    : 'Sem status';
  const canceledAtDate = allocation?.canceledAt ? toDate(allocation.canceledAt) : null;
  const canceledAtLabel = canceledAtDate
    ? (() => {
      try {
        return canceledAtDate.toLocaleString('pt-BR', {
          dateStyle: 'short',
          timeStyle: 'short',
        });
      } catch {
        return canceledAtDate.toISOString();
      }
    })()
    : null;


  const selectionCard = !isEditMode ? (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <h2 className="text-base font-semibold text-gray-900">Selecionar colaborador</h2>
        <p className="mt-1 text-sm text-gray-500">
          Escolha quem receberá a nova alocação antes de preencher os detalhes.
        </p>
        <div className="mt-4">
          <CollaboratorCombobox
            selectedId={comboboxSelectedId}
            onChange={(option) => setSelectedCollaboratorId(option?.id ?? '')}
            placeholder="Selecione o colaborador"
          />
        </div>
      </div>
    </div>
  ) : null;

  let mainContent: React.ReactNode;

  if (!activeCollaboratorId) {
    mainContent = (
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <p className="text-sm text-gray-500">
            Selecione um colaborador para visualizar e preencher o formulário de alocação.
          </p>
        </div>
      </div>
    );
  } else if (isLoading || (isEditMode && isLoadingAllocation)) {
    mainContent = (
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600" />
            <span className="ml-2 text-gray-500">
              {isEditMode ? 'Carregando dados da alocação...' : 'Carregando dados do colaborador...'}
            </span>
          </div>
        </div>
      </div>
    );
  } else if (isError || (isEditMode && isAllocationError)) {
    mainContent = (
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="py-12 text-center">
            <div className="mb-4 text-red-600">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 8.5c-.77.833-.231 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-medium text-gray-900">
              Erro ao carregar {isEditMode ? 'alocação' : 'colaborador'}
            </h3>
            <p className="text-gray-500">
              {(isEditMode ? allocationError : error) instanceof Error
                ? ((isEditMode ? allocationError : error) as Error).message
                : 'Não foi possível carregar os dados.'}
            </p>
          </div>
        </div>
      </div>
    );
  } else if (!collaborator) {
    mainContent = (
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="py-12 text-center">
            <div className="mb-4 text-yellow-600">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 8.5c-.77.833-.231 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-medium text-gray-900">Colaborador não encontrado</h3>
            <p className="text-gray-500">O colaborador informado não foi localizado.</p>
          </div>
        </div>
      </div>
    );
  } else if (isEditMode && !allocation && !isLoadingAllocation) {
    mainContent = (
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="py-12 text-center">
            <div className="mb-4 text-yellow-600">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 8.5c-.77.833-.231 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-medium text-gray-900">Alocação não encontrada</h3>
            <p className="text-gray-500">A alocação solicitada não foi localizada.</p>
          </div>
        </div>
      </div>
    );
  } else {
    mainContent = (
      <div className="space-y-6">
        <div className="bg-white shadow sm:rounded-lg">
          <CollaboratorInfoHeader
            photoUrl={(collaborator as any).photoURL ?? (collaborator as any).photoUrl ?? null}
            name={collaborator.name}
            email={collaborator.email}
          />
        </div>
        <CollaboratorAllocationForm
          collaboratorId={activeCollaboratorId!}
          collaboratorResources={(collaborator as any)?.resources ?? []}
          onSubmit={handleSubmit}
          onCancel={handleBack}
          isLoading={formIsSaving}
          initialValues={isEditMode ? allocationInitialValues : null}
          mode={isEditMode ? 'edit' : 'create'}
          terminationAppliedAt={isEditMode ? terminationAppliedAt : null}
        />
        {isEditMode && allocation ? (
          <section className="overflow-hidden rounded-lg border border-red-100 bg-white shadow-sm">
            <div className="border-b border-red-100 bg-red-50 px-4 py-4 sm:px-6">
              <h2 className="flex items-center gap-2 text-base font-semibold text-red-700">
                <XCircleIcon className="h-5 w-5" />
                Cancelar alocação
              </h2>
              <p className="mt-1 text-sm text-red-700">
                Encerrar a alocação remove o colaborador desse cliente e registra o histórico na timeline.
              </p>
            </div>
            <div className="space-y-4 px-4 py-5 sm:px-6">
              {isAllocationCancelled ? (
                <div className="space-y-3 text-sm text-gray-600">
                  <p className="font-medium text-gray-700">
                    Esta alocação já foi cancelada{canceledAtLabel ? ` em ${canceledAtLabel}` : ''}.
                  </p>
                  {allocation.cancelReason ? (
                    <p className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
                      <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Motivo informado
                      </span>
                      <span className="mt-1 block text-gray-700">{allocation.cancelReason}</span>
                    </p>
                  ) : (
                    <p className="text-sm text-gray-500">Nenhum motivo foi registrado.</p>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-gray-600">
                    <p>Ao confirmar, a alocação será marcada como cancelada imediatamente.</p>
                    <p className="mt-1">
                      Você poderá informar um motivo para facilitar o acompanhamento pela equipe.
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                      Status atual: {allocationStatusLabel}
                    </span>
                    <button
                      type="button"
                      onClick={handleOpenCancelDialog}
                      className="inline-flex items-center gap-2 rounded-md bg-red-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                      disabled={cancelAllocationMutation.isLoading}
                    >
                      <XCircleIcon className="h-4 w-4" />
                      Cancelar alocação
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        ) : null}
      </div>
    );
  }

  return (
    <>
      <button
        onClick={handleBack}
        className="mb-6 inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900"
      >
        <ArrowLeftIcon className="mr-2 h-5 w-5" />
        Voltar
      </button>

      <div className="space-y-6">
        {selectionCard}
        {mainContent}
      </div>

      <Transition.Root show={isCancelDialogOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleCloseCancelDialog}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50" />
          </Transition.Child>

          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-6">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative w-full max-w-lg transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all">
                  <div className="border-b border-gray-100 bg-red-50 px-4 py-4 sm:px-6">
                    <Dialog.Title className="flex items-center gap-2 text-lg font-semibold text-red-700">
                      <XCircleIcon className="h-6 w-6" />
                      Confirmar cancelamento
                    </Dialog.Title>
                    <p className="mt-1 text-sm text-red-700">
                      Essa ação encerra imediatamente a alocação e gera um registro na timeline do colaborador.
                    </p>
                  </div>

                  <div className="space-y-4 px-4 py-5 sm:px-6">
                    <div className="space-y-2">
                      <label htmlFor="cancel-reason" className="text-sm font-medium text-gray-700">
                        Informe o motivo (opcional)
                      </label>
                      <textarea
                        id="cancel-reason"
                        rows={4}
                        value={cancelReason}
                        onChange={(event) => setCancelReason(event.target.value)}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                        placeholder="Ex.: Cliente encerrou o contrato antecipadamente..."
                        disabled={cancelAllocationMutation.isLoading}
                      />
                      <p className="text-xs text-gray-500">
                        O motivo aparece na timeline e ajuda a monitorar casos semelhantes no futuro.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 border-t border-gray-100 bg-gray-50 px-4 py-4 sm:flex-row sm:justify-end sm:px-6">
                    <button
                      type="button"
                      onClick={handleCloseCancelDialog}
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400"
                      disabled={cancelAllocationMutation.isLoading}
                    >
                      Voltar
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmCancelAllocation}
                      className="inline-flex items-center justify-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 disabled:cursor-not-allowed disabled:opacity-70"
                      disabled={cancelAllocationMutation.isLoading}
                    >
                      {cancelAllocationMutation.isLoading ? (
                        <>
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          Cancelando...
                        </>
                      ) : (
                        <>
                          <XCircleIcon className="h-4 w-4" />
                          Confirmar cancelamento
                        </>
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Fluxo pós-salvar: pergunta se a mudança na alocação implica em Solicitação de
          equipamento no SI — ver handleSubmit. "Fechar" (X/fora do modal) equivale a "Não",
          sem abrir nada. */}
      {wizard?.etapa === 'motivo' && (
        <MotivoEncerramentoModal
          isOpen
          onClose={finalizarWizardSemSolicitacao}
          temOutrasAlocacoesAtivas={temOutrasAlocacoesAtivas}
          onEscolher={(motivo: MotivoEncerramentoAlocacao) => {
            if (motivo === 'desligamento') {
              abrirNovaSolicitacao({
                tipoSolicitacao: 'desligamento',
                clienteId: wizard!.clienteId,
                clienteNome: wizard!.clienteNome,
                alocacaoId: wizard!.alocacaoId,
              });
            } else if (motivo === 'saida_projeto') {
              setWizard({ ...wizard!, etapa: 'confirmarRecolhimento' });
            } else {
              // Mudança de Projeto: não pergunta nada aqui — primeiro precisa da alocação
              // nova (novo Cliente), só depois pergunta sobre equipamento (ver
              // mudancaDeProjetoOrigemAlocacaoId, lido no próximo carregamento desta página).
              const alocacaoOrigemId = wizard!.alocacaoId;
              const clienteOrigemId = wizard!.clienteId;
              const clienteOrigemNome = wizard!.clienteNome;
              setWizard(null);
              navigate(`/collaborators/${activeCollaboratorId}/allocations/new`, {
                state: {
                  mudancaDeProjetoOrigemAlocacaoId: alocacaoOrigemId,
                  mudancaDeProjetoClienteOrigemId: clienteOrigemId,
                  mudancaDeProjetoClienteOrigemNome: clienteOrigemNome,
                },
              });
            }
          }}
        />
      )}

      {wizard?.etapa === 'confirmarRecolhimento' && (
        <ConfirmarEquipamentoModal
          isOpen
          titulo="Recolhimento de equipamento"
          pergunta="Haverá necessidade de recolhimento de equipamento?"
          onClose={finalizarWizardSemSolicitacao}
          onResponder={(sim) => {
            if (sim) {
              abrirNovaSolicitacao({
                tipoSolicitacao: 'devolucao',
                clienteId: wizard!.clienteId,
                clienteNome: wizard!.clienteNome,
                alocacaoId: wizard!.alocacaoId,
              });
            } else {
              finalizarWizardSemSolicitacao();
            }
          }}
        />
      )}

      {wizard?.etapa === 'confirmarEquipamentoNovo' && (
        <ConfirmarEquipamentoModal
          isOpen
          titulo="Equipamento"
          pergunta="Há necessidade de envio de equipamento?"
          onClose={finalizarWizardSemSolicitacao}
          onResponder={(sim) => {
            if (sim) {
              abrirNovaSolicitacao({
                tipoSolicitacao: 'nova_demanda',
                clienteId: wizard!.clienteId,
                clienteNome: wizard!.clienteNome,
                alocacaoId: wizard!.alocacaoId,
              });
            } else {
              finalizarWizardSemSolicitacao();
            }
          }}
        />
      )}

      {wizard?.etapa === 'confirmarTrocaEquipamento' && (
        <ConfirmarEquipamentoModal
          isOpen
          titulo="Troca de equipamento"
          pergunta="Haverá necessidade de troca do equipamento?"
          onClose={finalizarWizardSemSolicitacao}
          onResponder={(sim) => {
            if (sim) {
              abrirNovaSolicitacao({
                tipoSolicitacao: 'mudanca_projeto',
                clienteId: wizard!.clienteId,
                clienteNome: wizard!.clienteNome,
                alocacaoId: wizard!.alocacaoId,
                clienteDevolucaoId: wizard!.clienteOrigemId,
                clienteDevolucaoNome: wizard!.clienteOrigemNome,
              });
            } else {
              // Não precisa trocar o equipamento — ainda assim pode precisar reformatar o
              // que já está com o colaborador pro novo projeto (stack/imagem diferente).
              setWizard({ ...wizard!, etapa: 'confirmarFormatacao' });
            }
          }}
        />
      )}

      {wizard?.etapa === 'confirmarFormatacao' && (
        <ConfirmarEquipamentoModal
          isOpen
          titulo="Formatação de equipamento"
          pergunta="Haverá necessidade de formatação do equipamento?"
          onClose={finalizarWizardSemSolicitacao}
          onResponder={(sim) => {
            if (sim) {
              abrirNovaSolicitacao({
                tipoSolicitacao: 'formatacao',
                clienteId: wizard!.clienteId,
                clienteNome: wizard!.clienteNome,
                alocacaoId: wizard!.alocacaoId,
              });
            } else {
              finalizarWizardSemSolicitacao();
            }
          }}
        />
      )}

      <NovaSolicitacaoDrawer
        isOpen={novaSolicitacaoDrawer != null}
        valoresIniciais={novaSolicitacaoDrawer ?? undefined}
        onClose={() => {
          setNovaSolicitacaoDrawer(null);
          if (activeCollaboratorId) {
            navigate(`/collaborators/${activeCollaboratorId}/allocations`);
          }
        }}
        onCriada={(solicitacaoId) => {
          setNovaSolicitacaoDrawer(null);
          setAcompanharSolicitacaoId(solicitacaoId);
        }}
      />

      <SolicitacaoDrawer
        solicitacaoId={acompanharSolicitacaoId}
        forcarVisaoSolicitante
        onClose={() => {
          setAcompanharSolicitacaoId(null);
          if (activeCollaboratorId) {
            navigate(`/collaborators/${activeCollaboratorId}/allocations`);
          }
        }}
      />
    </>
  );
}
